import { useCallback, useMemo } from 'react';
import { useDrag, useDrop } from 'react-dnd';

export const useVerticalDnD = ({ element, type, id, parentId, index, enabled = true, itemProps, canMove, moveItem }: any) => {
    const handleMove = useCallback((item, monitor, isDrop) => {
        if (!element.current && !isDrop) {
            return;
        }

        if (canMove) {
            if (!canMove(itemProps, item.itemProps)) {
                return;
            }
        }

        const dragIndex = item.index;
        const hoverIndex = index;

        const dragParentId = item.parentId;
        const hoverParentId = parentId;

        if (!isDrop) {
            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = element.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            if (dragParentId === hoverParentId && dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            if (dragParentId === hoverParentId && dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
        }

        moveItem(item.id || dragIndex, id || hoverIndex, isDrop);
        item.index = hoverIndex;
        item.parentId = hoverParentId;
    }, [index, id, parentId, itemProps, canMove, moveItem]);

    const dropSpec = useMemo(() => {
        return {
            accept: type,
            hover(item, monitor) {
                handleMove(item, monitor);
            },
            collect: monitor => ({
                isOver: monitor.isOver(),
            }),
            drop(item, monitor) {
                handleMove(item, monitor, true);
            }
        }
    }, [handleMove]);

    const [{ isOver }, drop] = useDrop(dropSpec);

    const dragSpec = useMemo(() => {
        return {
            type,
            item: { id, parentId, index, itemProps },
            collect: monitor => ({
                isDragging: monitor.isDragging(),
            }),
            end(item, monitor) {
                if (monitor.didDrop()) {
                    return false;
                }

                handleMove(item, monitor, true);
            },
            canDrag: () => enabled,
            isDragging: !id ? undefined : monitor => monitor.getItem()?.id === id
        }
    }, [type, id, parentId, index, itemProps, enabled]);

    const [{ isDragging }, drag, preview] = useDrag(dragSpec);

    return {
        drag,
        drop,
        preview,
        isDragging,
        isOver
    };
}
